/**
 * Simplified DOM handling utilities
 */
import { createEffect, currentEffect } from '../reactive';
import { isSignal, getSignalValue } from '../reactive/utils';
import { Signal, Getter } from '../reactive';
import { isSignalGetter } from '../reactive/signal';
import { DEBUG_MODE, debugLog, debugError } from '../reactive/debug';

// Type definitions for hyperscript
type Child = Node | string | number | boolean | null | undefined | Signal<any> | Getter<any> | any[];
type Props = Record<string, any>;

// Store of elements that have signal attributes
const signalElementRegistry = new Map<HTMLElement, Map<string, Signal<any>>>();

/**
 * Create an HTML element with the given tag name, props, and children
 */
export function h(tag: string, props?: Props, ...children: Child[]): HTMLElement {
    if (DEBUG_MODE) debugLog('[DOM] h called with:', tag, props, children);

    const element = document.createElement(tag);

    // Set properties and attributes on the element
    if (props) {
        for (const [key, value] of Object.entries(props)) {
            if (key === 'style' && typeof value === 'object') {
                // Handle style object
                Object.assign(element.style, value);
            } else if (key.startsWith('on') && typeof value === 'function') {
                // Handle event listeners
                const eventName = key.slice(2).toLowerCase();
                element.addEventListener(eventName, value as EventListener);
            } else if (isSignal(value)) {
                // Handle signals
                const initialValue = getSignalValue(value);
                updateProperty(element, key, initialValue);

                // Track signals for this element
                if (!signalElementRegistry.has(element)) {
                    signalElementRegistry.set(element, new Map());
                }
                signalElementRegistry.get(element)!.set(key, value);

                // Create an effect to keep the DOM updated
                createEffect(() => {
                    const signalValue = getSignalValue(value);
                    updateProperty(element, key, signalValue);
                });
            } else {
                // Handle regular properties and attributes
                if (key === 'class' || key === 'className') {
                    element.className = String(value);
                } else if (key.startsWith('data-')) {
                    element.setAttribute(key, String(value));
                } else {
                    (element as any)[key] = value;
                }
            }
        }
    }

    // Add children
    for (const child of children) {
        appendChildToElement(element, child);
    }

    return element;
}

/**
 * Check if a value is a signal or a function (which might be a signal accessor)
 */
function isSignalOrFunction(value: any): boolean {
    // Skip specific values that we know are not reactive
    if (value === null || value === undefined ||
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean') {
        return false;
    }

    // Check if it's a signal (tuple or marked getter function)
    if (isSignal(value)) {
        return true;
    }

    // Check if it's a function that might be a signal getter
    if (typeof value === 'function') {
        // If it's marked as a signal getter, it's definitely reactive
        if (isSignalGetter(value)) {
            return true;
        }
        // For other functions, we'll treat them as potentially reactive
        // This handles cases where functions might be signal getters
        return true;
    }

    return false;
}

/**
 * Append a child to an element, with special handling for different types
 */
function appendChildToElement(element: HTMLElement, child: Child): void {
    if (child == null) return;

    // Handle arrays (multiple children or from conditional expressions)
    if (Array.isArray(child)) {
        for (const subChild of child) {
            appendChildToElement(element, subChild);
        }
        return;
    }

    // Handle DOM nodes directly
    if (child instanceof Node) {
        element.appendChild(child);
        return;
    }

    // Handle signals and functions (both {count} and {count()} syntaxes)
    if (isSignalOrFunction(child)) {
        const container = document.createElement('span');
        container.style.display = 'contents'; // Make it invisible in the layout
        element.appendChild(container);

        // Create an effect to update when signal changes
        createEffect(() => {
            if (DEBUG_MODE) {
                debugLog('[DOM] Effect running for reactive child:', typeof child);
                debugLog('[DOM] currentEffect during effect:', currentEffect);
            }
            
            // FIXED: Call the signal getter INSIDE the effect to establish dependency
            let value: any;
            
            if (typeof child === 'function') {
                if (DEBUG_MODE) debugLog('[DOM] Calling function inside effect');
                value = child(); // This will track the dependency
            } else if (Array.isArray(child) && typeof child[0] === 'function') {
                if (DEBUG_MODE) debugLog('[DOM] Calling signal tuple getter inside effect');
                value = child[0](); // This will track the dependency
            } else if (isSignal(child)) {
                if (DEBUG_MODE) debugLog('[DOM] Getting signal value inside effect');
                value = getSignalValue(child); // This will track the dependency
            } else {
                value = child;
            }
            
            if (DEBUG_MODE) debugLog('[DOM] Effect got value:', value);

            // Clear previous content
            container.innerHTML = '';

            // Handle different types of values
            if (value instanceof Node) {
                container.appendChild(value);
            } else if (value instanceof Element) {
                container.appendChild(value);
            } else if (Array.isArray(value)) {
                for (const item of value) {
                    appendChildToElement(container, item);
                }
            } else if (value === false || value === null || value === undefined) {
                // For falsy values in conditional rendering, don't render anything
            } else if (value != null) {
                container.textContent = String(value);
            }
        });
        return;
    }

    // Handle primitive values
    element.appendChild(document.createTextNode(String(child)));
}

/**
 * Helper to update a property or attribute on an element
 */
function updateProperty(element: HTMLElement, key: string, value: any) {
    if (key === 'class' || key === 'className') {
        element.className = String(value);
    } else if (key === 'style' && typeof value === 'object') {
        Object.assign(element.style, value);
    } else if (key === 'disabled' || key === 'checked' || key === 'required') {
        // Handle boolean attributes
        if (value) {
            element.setAttribute(key, '');
        } else {
            element.removeAttribute(key);
        }
    } else if (key.startsWith('data-')) {
        element.setAttribute(key, String(value));
    } else {
        // For other properties, set them directly
        (element as any)[key] = value;
        // Also set attribute for visibility in DOM
        if (typeof value !== 'object' && value !== undefined) {
            element.setAttribute(key, String(value));
        }
    }
}

/**
 * Mount a component to a DOM element
 */
export function mount(component: () => any, container: HTMLElement) {
    const element = component();
    container.appendChild(element);
    return element;
}

/**
 * Query selector helper that returns an element
 */
export function query(selector: string): HTMLElement | null {
    return document.querySelector(selector);
}

/**
 * Query selector that returns multiple elements
 */
export function queryAll(selector: string): HTMLElement[] {
    return Array.from(document.querySelectorAll(selector));
}

/**
 * Creates a container element and initializes the app inside it
 */
export function createApp(rootComponent: () => any, containerSelector: string = '#app'): void {
    const container = query(containerSelector);
    if (!container) {
        debugError(`Container element "${containerSelector}" not found`);
        return;
    }
    mount(rootComponent, container);
}
